import React, { createContext, useContext, useState, useCallback } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastContextType {
    // `unknown`, not `string`: callers already pass the API's error envelope object here.
    showToast: (message: unknown, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastType, setToastType] = useState<ToastType>('success');

    // ⛔ COERCE, DO NOT TRUST THE TYPE. Five create pages call
    // `showToast(err.response?.data?.error ?? 'Failed...')`, and for any endpoint answering with
    // redu's error envelope that argument is an OBJECT, not a string. React throws
    // "Objects are not valid as a React child" and the page goes blank instead of showing the
    // refusal. Unwrapping the envelope here fixes every caller at once, and the message it picks is
    // the one the API wrote for a human to read.
    const showToast = useCallback((message: unknown, type: ToastType = 'success') => {
        let text: string;
        if (typeof message === 'string') {
            text = message;
        } else if (message && typeof message === 'object') {
            const m = message as any;
            text = m.message || m.error?.message || m.error || 'Something went wrong.';
            if (typeof text !== 'string') text = 'Something went wrong.';
            // ⛔ THE HINT LIVES INSIDE THE ENVELOPE TOO. Reading only `m.hint` dropped it for every
            // error shaped `{ error: { code, message, hint } }`, which is every /v1 refusal: the user
            // saw WHAT was refused and never the sentence telling them how to fix it. A card_declined
            // block is unrecoverable-looking without it.
            const hint = (typeof m.hint === 'string' && m.hint) || (typeof m.error?.hint === 'string' && m.error.hint);
            if (hint) text = `${text} ${hint}`;
        } else {
            text = String(message ?? '');
        }
        setToastMessage(text);
        setToastType(type);
        setTimeout(() => setToastMessage(null), 5000);
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {toastMessage && (
                <div className={`floating-message ${toastType}`}>
                    {toastMessage}
                </div>
            )}
        </ToastContext.Provider>
    );
};

export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (!context) throw new Error('useToast must be used within a ToastProvider');
    return context;
};
